// ---------------------------------------------------------------------------
// step-4-review.tsx — Review + Create step.
// Compact summary of all choices, single "Create project" CTA, loading state,
// success overlay, and inline error with retry.
//
// Create logic lives in the parent (new-project-wizard-page.tsx) so both the
// content CTA and the footer "Create project" button share one handler.
// ---------------------------------------------------------------------------

import React from 'react'
import styles from '../new-project-wizard-page.module.css'
import { useWizard } from '../wizard-state'
import { formatRuntimeTargets } from '../../lib/studio-project-plan'
import { useStudioLocale } from '@/i18n'
import type { PlanningEvidence } from '../../lib/planning-evidence'
export type { PlanningEvidence } from '../../lib/planning-evidence'

interface Step4ReviewProps {
  success: boolean
  createdProjectId: string | null
  mode: 'generate' | 'project'
  progressMessage: string | null
  planningEvidence: PlanningEvidence | null
  onCreateClick: () => void
}

export function Step4Review({
  success,
  createdProjectId,
  mode,
  progressMessage,
  planningEvidence,
  onCreateClick,
}: Step4ReviewProps) {
  const { strings } = useStudioLocale()
  const copy = strings.newProject.step4
  // The two summary rows this step shares with step 3 read step 3's keys —
  // one declaration site for `Starting point` / `Starter` / `Style`, so the
  // two screens cannot drift into naming the same row differently.
  const shared = strings.newProject.step3
  const { state } = useWizard()

  const dsName =
    state.source === 'template'
      ? shared.fromStarterSuffix(state.selectedTemplateName ?? '')
      : state.selectedDesignSystemRelease
        ? copy.approvedReleaseSuffix(
            state.selectedDesignSystemReleaseName ??
              state.selectedDesignSystemRelease.packageId,
            state.selectedDesignSystemRelease.packageVersion,
          )
        : state.selectedPresetName
          ? copy.personalStyleSuffix(state.selectedPresetName)
          : '—'

  if (success) {
    return (
      <div className={styles.successScreen} aria-live="polite">
        <div className={styles.successIcon} aria-hidden="true">
          <svg viewBox="0 0 24 24" fill="none">
            <circle
              cx="12"
              cy="12"
              r="11"
              stroke="currentColor"
              strokeWidth="1.5"
            />
            <path
              d="M7 12l3.5 3.5L17 9"
              stroke="currentColor"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </div>
        <h2 className={styles.successTitle}>
          {mode === 'generate' ? copy.successGenerate : copy.successProject}
        </h2>
        <p className={styles.successSub}>
          {mode === 'generate' ? copy.openingWorkspace : copy.openingEditor}
        </p>
        <p className={styles.successId}>{createdProjectId}</p>
      </div>
    )
  }

  return (
    <div className={styles.stepContent}>
      <div className={styles.stepHeading}>
        <h1 className={styles.stepH1}>{copy.heading}</h1>
        <p className={styles.stepLead}>
          {mode === 'generate' ? copy.leadGenerate : copy.leadProject}
        </p>
      </div>

      {/* Summary */}
      <div className={[styles.summaryCard, styles.reviewSummary].join(' ')}>
        <div className={styles.summaryRow}>
          <span className={styles.summaryKey}>
            {shared.summaryStartingPoint}
          </span>
          <span className={styles.summaryVal}>
            {state.starterPathName ??
              (state.source === 'template'
                ? shared.starterFallback
                : shared.fromScratch)}
          </span>
        </div>
        {state.source === 'template' && state.selectedTemplateName && (
          <div className={styles.summaryRow}>
            <span className={styles.summaryKey}>{shared.summaryStarter}</span>
            <span className={styles.summaryVal}>
              {state.selectedTemplateName}
            </span>
          </div>
        )}
        <div className={styles.summaryRow}>
          <span className={styles.summaryKey}>{shared.summaryStyle}</span>
          <span className={styles.summaryVal}>{dsName}</span>
        </div>
        <div className={styles.summaryRow}>
          <span className={styles.summaryKey}>{copy.summaryName}</span>
          <span className={styles.summaryVal}>{state.projectName || '—'}</span>
        </div>
        {state.projectDescription && (
          <div className={styles.summaryRow}>
            <span className={styles.summaryKey}>
              {copy.summaryWhatItShouldDo}
            </span>
            <span className={styles.summaryVal}>
              {state.projectDescription}
            </span>
          </div>
        )}
        <div className={styles.summaryRow}>
          <span className={styles.summaryKey}>{copy.summaryRunsOn}</span>
          <span className={styles.summaryVal}>
            {formatRuntimeTargets(state.runtimeTargets)}
          </span>
        </div>
        <div className={styles.summaryRow}>
          <span className={styles.summaryKey}>{shared.workType}</span>
          <span className={styles.summaryVal}>
            {shared.appClass[state.appClass].label}
          </span>
        </div>
        <div className={styles.summaryRow}>
          <span className={styles.summaryKey}>{shared.reviewLevel}</span>
          <span className={styles.summaryVal}>
            {shared.security[state.securityProfile].label}
          </span>
        </div>
      </div>

      {mode === 'generate' && (
        <div className={styles.generationNote}>
          <span className={styles.generationNoteTitle}>
            {copy.generationNoteTitle}
          </span>
          <span className={styles.generationNoteBody}>
            {copy.generationNoteBody}
          </span>
        </div>
      )}

      {mode === 'generate' && planningEvidence && (
        <div
          className={[
            styles.planningEvidence,
            planningEvidence.status === 'live'
              ? styles.planningEvidenceLive
              : '',
            planningEvidence.status === 'fallback'
              ? styles.planningEvidenceFallback
              : '',
          ]
            .filter(Boolean)
            .join(' ')}
          data-testid="new-project-planning-evidence"
          data-planning-status={planningEvidence.status}
          role="status"
        >
          <span className={styles.planningEvidenceTitle}>
            {planningEvidence.title}
          </span>
          <span className={styles.planningEvidenceDetail}>
            {planningEvidence.detail}
          </span>
        </div>
      )}

      {state.isCreating && progressMessage && (
        <div className={styles.loadingRow} role="status">
          <span className={styles.spinner} aria-hidden="true" />
          {progressMessage}
        </div>
      )}

      {/* Error */}
      {state.createError && (
        <div className={styles.errorBanner} role="alert">
          <span>{state.createError}</span>
          <button
            type="button"
            className={styles.retryBtn}
            onClick={onCreateClick}
            disabled={state.isCreating}
          >
            {copy.retry}
          </button>
        </div>
      )}
    </div>
  )
}
